import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { PlusLg, Trash3, CheckCircle } from 'react-bootstrap-icons';

import { useLanguage } from '../../../../../API/Context/LanguageContext';
import { useUser } from '../../../../../API/Context/UserContext';
import { useUserMeQualifications } from '../../../../../API/Hooks/useUserMeQualifications';
import VocabProMenu from '../../../../../ConfigFiles/multilingual/VocabProMenu';
import { VocabButtons as btn_vocab } from '../../../../../ConfigFiles/multilingual/VocabButtons';
import EnumSelect from '../../../LoggedUserGeneral/ProfileOnboarding/Steps/Widgets/EnumSelect';
import { EducationEnum } from '../../../../../Types/Enums/EducationEnum';
import { EDUCATION_CONFIG } from '../../../../../ConfigFiles/maps/education_map';

interface QualificationsWidgetProps {
  onSaveSuccess?: () => void;
}

interface LocalQualification {
  localId: string;
  id?: string | null;
  title: string;
  institution: string;
  category: number;
  year: string;
  credential_id: string;
  deleted?: boolean;
}

// Filter EDUCATION_CONFIG to include only Professional/Bachelors, Masters, and Doctorate
const QUALIFICATION_EDUCATION_OPTIONS = EDUCATION_CONFIG.filter((opt) =>
  [EducationEnum.BACHELORS, EducationEnum.MASTERS, EducationEnum.DOCTORATE].includes(
    opt.id as EducationEnum
  )
);

export const QualificationsWidget: React.FC<QualificationsWidgetProps> = ({ onSaveSuccess }) => {
  const { t } = useLanguage();
  const { user, updateProfileField } = useUser();
  const { updateQualifications, loading: isSavingHook } = useUserMeQualifications();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qualifications, setQualifications] = useState<LocalQualification[]>([]);

  useEffect(() => {
    if (user?.professional?.qualifications) {
      const mapped = user.professional.qualifications.map((q: any, idx: number) => ({
        localId: q.id || `qual-${idx}`,
        id: q.id || null,
        title: q.title || '',
        institution: q.institution || '',
        category: q.category ?? EducationEnum.BACHELORS,
        year: q.year ? String(q.year) : '',
        credential_id: q.credential_id || '',
        deleted: false,
      }));
      setQualifications(mapped);
    }
  }, [user]);

  const handleChange = (localId: string, field: keyof LocalQualification, value: any) => {
    setQualifications((prev) =>
      prev.map((q) => {
        if (q.localId !== localId) return q;
        if (field === 'year') {
          value = String(value).replace(/\D/g, '').substring(0, 4);
        }
        return { ...q, [field]: value };
      })
    );
  };

  const handleAdd = () => {
    setQualifications((prev) => [
      ...prev,
      {
        localId: `new-${Date.now()}`,
        id: null,
        title: '',
        institution: '',
        category: EducationEnum.BACHELORS,
        year: '',
        credential_id: '',
        deleted: false,
      },
    ]);
  };

  const handleRemove = (localId: string) => {
    setQualifications((prev) =>
      prev
        .map((q) => (q.localId === localId ? (q.id ? { ...q, deleted: true } : null) : q))
        .filter(Boolean) as LocalQualification[]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = qualifications
        .filter((q) => !q.deleted)
        .map((q) => ({
          title: q.title,
          institution: q.institution,
          category: Number(q.category),
          year: Number(q.year) || 0,
          credential_id: q.credential_id,
          ...(q.id && { id: q.id }),
        }));

      const updatedUser = await updateQualifications(payload);

      if (user?.professional) {
        updateProfileField('professional', {
          ...user.professional,
          qualifications: updatedUser.professional?.qualifications ?? payload,
        });
      }

      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      console.error(err);
      setError(t(VocabProMenu, 'qualifications_missing_fields'));
    } finally {
      setSaving(false);
    }
  };

  const activeQualifications = qualifications.filter((q) => !q.deleted);
  const isSaving = saving || isSavingHook;

  return (
    <div className="d-flex flex-column gap-4 animate-in">
      {/* Subheader Bar: Section Description Left | Save Button Right */}
      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div>
          <p className="m-0 text-muted small">{t(VocabProMenu, 'qualifications_desc')}</p>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="d-flex align-items-center gap-2 shadow-sm"
        >
          {isSaving ? <Spinner size="sm" animation="border" /> : <CheckCircle size={16} />}
          <span>{t(btn_vocab, 'save')}</span>
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Main Qualifications List */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <h5 className="m-0 fw-bold">{t(VocabProMenu, 'qualifications_list')}</h5>
            <Badge bg="secondary">{activeQualifications.length}</Badge>
          </div>
          <Button variant="outline-primary" size="sm" onClick={handleAdd} className="d-flex align-items-center gap-1">
            <PlusLg size={14} />
            <span>{t(VocabProMenu, 'qualification_new')}</span>
          </Button>
        </Card.Header>

        <Card.Body>
          {activeQualifications.length === 0 ? (
            <p className="text-center text-muted py-4 m-0">
              {t(VocabProMenu, 'no_qualifications')}
            </p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {activeQualifications.map((q) => (
                <div key={q.localId} className="p-3 border rounded bg-light position-relative">
                  <Button
                    variant="link"
                    className="text-danger position-absolute top-0 end-0 m-2 p-1"
                    onClick={() => handleRemove(q.localId)}
                  >
                    <Trash3 size={16} />
                  </Button>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="small fw-semibold">
                        {t(VocabProMenu, 'qualifications_title')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={q.title}
                        onChange={(e) => handleChange(q.localId, 'title', e.target.value)}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="small fw-semibold">
                        {t(VocabProMenu, 'qualifications_institution')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={q.institution}
                        onChange={(e) => handleChange(q.localId, 'institution', e.target.value)}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label className="small fw-semibold">
                        {t(VocabProMenu, 'qualifications_category')}
                      </Form.Label>
                      <EnumSelect
                        value={q.category}
                        options={QUALIFICATION_EDUCATION_OPTIONS}
                        onChange={(val) => handleChange(q.localId, 'category', val)}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label className="small fw-semibold">
                        {t(VocabProMenu, 'qualifications_year_of_completion')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        maxLength={4}
                        value={q.year}
                        onChange={(e) => handleChange(q.localId, 'year', e.target.value)}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label className="small fw-semibold">
                        {t(VocabProMenu, 'qualifications_license')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={q.credential_id}
                        onChange={(e) => handleChange(q.localId, 'credential_id', e.target.value)}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default QualificationsWidget;